import { createElement, memo, useMemo, useReducer } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { ChevronDown } from 'lucide-react';

import css from '../BurgerMenu.module.css';

import { isUserAdminAtom } from 'v2/atoms/userAtom';
import { UserRole } from 'v2/constants';
import { NavigationParent } from 'v2/types';
import { isRouteAllowed } from 'v2/utils';

interface BurgerMenuCollapseProps {
  userRoles: UserRole[];
  navigationItem: NavigationParent;
  onClose: VoidFunction;
}

export const ExpandableMenuItem = memo<BurgerMenuCollapseProps>(({ userRoles, navigationItem, onClose }) => {
  const location = useLocation();

  const isUserAdmin = useAtomValue(isUserAdminAtom);

  const [isExpanded, setExpanded] = useReducer(
    (state) => !state,
    !!navigationItem.children?.some((c) => location.pathname.includes(c.path))
  );

  const isAtLeastOneAllowed = useMemo(() => {
    if (isUserAdmin) {
      return true;
    }

    return isRouteAllowed(userRoles, navigationItem.group);
  }, [navigationItem, isUserAdmin]);

  return (
    isAtLeastOneAllowed && (
      <div>
        <button
          key={navigationItem.title}
          className={clsx(css.burgerMenuItem, css.asButton, isExpanded && css.active)}
          onClick={setExpanded}
        >
          <p className={css.burgerMenuItemContent}>
            {createElement(navigationItem.icon, { className: css.burgerMenuItemIcon })} {navigationItem.title}
          </p>

          <ChevronDown className={clsx(css.burgerMenuChevron, isExpanded && css.rotate)} width={20} height={20} />
        </button>

        <div className={clsx(css.burgerMenuItemExpandable, isExpanded && css.expanded)}>
          {navigationItem.children?.map(
            (c) =>
              isRouteAllowed(userRoles, c.group) && (
                <NavLink
                  key={c.title}
                  className={({ isActive }) => clsx(css.burgerMenuItem, css.asInnerLink, isActive && css.active)}
                  to={c.path ?? ''}
                  onClick={onClose}
                >
                  {c.title}
                </NavLink>
              )
          )}
        </div>
      </div>
    )
  );
});

ExpandableMenuItem.displayName = 'ExpandableMenuItem';
